import type { Locale, Namespace } from "./locales";

/**
 * 카탈로그 조각 로더 (ADR 0025 · Issue #13)
 * --------------------------------------------------------------------------
 * 언어 × 이름공간마다 **별도 chunk**입니다. 리터럴 지정자만 쓰는 동적 import 표를
 * 두는 이유는 번들러가 경로를 정적으로 볼 수 있어야 조각을 나눌 수 있기 때문입니다 —
 * 템플릿 문자열로 만들면 한 덩어리로 합쳐지거나 전부 초기 번들에 실립니다.
 *
 * 고른 언어의 조각만 내려받습니다. 대체 언어(en) 조각은 **찾는 ID가 실제로 빠졌을
 * 때만** 뒤따라 내려받습니다 — 카탈로그 parity 게이트가 그 상황 자체를 막으므로
 * 평상시에는 네트워크가 한 벌뿐입니다.
 *
 * 메모리는 유계입니다: 언어 수 × 이름공간 수(현재 2 × 12)를 넘을 수 없습니다.
 */
export type Catalog = Readonly<Record<string, string>>;

type Loader = () => Promise<{ default: Catalog }>;

const LOADERS: Record<Locale, Record<Namespace, Loader>> = {
  ko: {
    agent: () => import("./messages/ko/agent"),
    approve: () => import("./messages/ko/approve"),
    common: () => import("./messages/ko/common"),
    events: () => import("./messages/ko/events"),
    execsession: () => import("./messages/ko/execsession"),
    nav: () => import("./messages/ko/nav"),
    palette: () => import("./messages/ko/palette"),
    pod: () => import("./messages/ko/pod"),
    resourcemap: () => import("./messages/ko/resourcemap"),
    resources: () => import("./messages/ko/resources"),
    terminal: () => import("./messages/ko/terminal"),
    workload: () => import("./messages/ko/workload"),
  },
  en: {
    agent: () => import("./messages/en/agent"),
    approve: () => import("./messages/en/approve"),
    common: () => import("./messages/en/common"),
    events: () => import("./messages/en/events"),
    execsession: () => import("./messages/en/execsession"),
    nav: () => import("./messages/en/nav"),
    palette: () => import("./messages/en/palette"),
    pod: () => import("./messages/en/pod"),
    resourcemap: () => import("./messages/en/resourcemap"),
    resources: () => import("./messages/en/resources"),
    terminal: () => import("./messages/en/terminal"),
    workload: () => import("./messages/en/workload"),
  },
};

const loaded = new Map<string, Catalog>();
const inflight = new Map<string, Promise<Catalog>>();
/** 테스트가 갈아끼운 로더. 실패 경로를 실제로 태우기 위한 seam이며 비어 있는 것이 기본입니다. */
const overrides = new Map<string, Loader>();

function keyOf(locale: Locale, namespace: Namespace): string {
  return `${locale}/${namespace}`;
}

/** 이미 손에 있는 조각. 없으면 undefined이고 렌더를 막지 않습니다. */
export function catalogOf(locale: Locale, namespace: Namespace): Catalog | undefined {
  return loaded.get(keyOf(locale, namespace));
}

/**
 * 조각을 내려받습니다. 같은 조각의 동시 요청은 **하나로 합쳐집니다** —
 * 화면 여러 곳이 같은 이름공간을 동시에 쓰면 요청이 그만큼 나가면 안 됩니다.
 * 실패는 캐시하지 않으므로 다음 요청이 다시 시도할 수 있습니다(자동 재시도는 없음).
 */
export function loadCatalog(locale: Locale, namespace: Namespace): Promise<Catalog> {
  const key = keyOf(locale, namespace);
  const already = loaded.get(key);
  if (already) return Promise.resolve(already);
  const running = inflight.get(key);
  if (running) return running;
  const promise = (overrides.get(key) ?? LOADERS[locale][namespace])()
    .then((module) => {
      loaded.set(key, module.default);
      return module.default;
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, promise);
  return promise;
}

/**
 * 조각을 미리 채웁니다. 테스트가 동기적으로 카탈로그를 올릴 때 씁니다 —
 * 프로덕션 경로는 `loadCatalog`만 씁니다.
 */
export function primeCatalog(locale: Locale, namespace: Namespace, catalog: Catalog): void {
  loaded.set(keyOf(locale, namespace), catalog);
}

/**
 * 테스트 전용 — 조각 로더를 갈아끼웁니다. 조각을 못 받는 상황(404·네트워크 단절)을
 * 실제로 태워 보려면 실패하는 로더가 필요합니다.
 */
export function setCatalogLoaderForTest(locale: Locale, namespace: Namespace, loader: Loader): void {
  overrides.set(keyOf(locale, namespace), loader);
}

/** 테스트 전용 — 모듈 캐시와 갈아끼운 로더를 비웁니다. 프로덕션 경로에서 호출하지 않습니다. */
export function resetCatalogsForTest(): void {
  loaded.clear();
  inflight.clear();
  overrides.clear();
}
